import { FieldArray } from 'formik';
import React, { useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { Link } from 'react-router-dom';
import { array, number, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { Campo, Contexto } from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Modal from 'Component/Modal';
import Holerite from 'Interface/Holerites';
import Item from 'Interface/Itens';
import VinculoEmpregaticio from 'Interface/VinculosEmpregaticios';
import {
  useAdicionarHoleriteMutation,
  useRecuperarHoleritesQuery,
} from 'View/Service/Default/Holerites';

interface Props {
  dados: VinculoEmpregaticio;
  mes: number;
  ano: number;
  tipo: number;
}

const regras = object({
  salario_base: number().required('Obrigatório'),
  base_calculo_fgts: number().required('Obrigatório'),
  fgts_mes: number().required('Obrigatório'),
  base_calculo_inss: number().required('Obrigatório'),
  base_calculo_irrf: number().required('Obrigatório'),
  total_de_vencimentos: number().required('Obrigatório'),
  total_de_descontos: number().required('Obrigatório'),
  liquido: number().required('Obrigatório'),
  faixa_irrf: number().required('Obrigatório'),
  itens: array().of(
    object().shape({
      codigo: number().required('Obrigatório'),
      descricao: string().required('Obrigatório'),
      referencia: string().required('Obrigatório'),
      vencimentos: number().required('Obrigatório'),
      descontos: number().required('Obrigatório'),
    })
  ),
});

const UploadLista = ({ dados, mes, ano, tipo }: Props): React.ReactElement => {
  const toast = useToast();
  const [modal, setModal] = useState<boolean>(false);
  const [adicionarHolerite] = useAdicionarHoleriteMutation();
  const [vencimentos, setVencimentos] = useState<number>(0);
  const [descontos, setDescontos] = useState<number>(0);
  const [liquido, setLiquido] = useState<number>(0);

  const {
    data: { holerites: [holerite] } = {
      holerites: [{} as Holerite],
    },
  } = useRecuperarHoleritesQuery({
    options: { usuario_id: dados.usuario_id, ano: ano, mes: mes, tipo: tipo },
  });

  const enviar = async (dadosForm: Holerite): Promise<void> => {
    try {
      await adicionarHolerite({
        ...dadosForm,
        mes,
        ano,
        tipo,
        vinculo_empregaticio_id: dados.id,
      }).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      setModal(false);
    } catch (error) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const calcularValores = (holerite: Holerite): void => {
    let vencimentos = 0;
    let descontos = 0;

    holerite?.itens?.forEach((item: Item) => {
      vencimentos += parseFloat(item.vencimentos.toString());
      descontos += parseFloat(item.descontos.toString());
    });

    setVencimentos(vencimentos);
    setDescontos(descontos);
    setLiquido(vencimentos - descontos);
  };

  return (
    <React.Fragment>
      <div className="d-flex border-bottom align-items-center py-2 px-3">
        <Link to={`/usuarios/editar/${dados.usuario?.id}`}>
          <Imagem
            src={dados.usuario?.imagem}
            alt={dados.usuario?.nome}
            className="rounded-circle object-fit-cover"
            width="80"
            height="80"
          />
        </Link>
        <div className="ms-4 fs-4">
          <Link
            to={`/usuarios/editar/${dados.usuario?.id}`}
            className="text-decoration-none"
          >
            {dados.usuario?.nome}
          </Link>
          <small className="d-block fs-6 text-muted">
            {dados.empresa?.nome_fantasia}
          </small>
        </div>
        <BrowserView className="d-flex flex-grow-1 align-items-center justify-content-end">
          <div className="ms-5 fs-5 text-end">
            <div className="fs-4">
              <div className="badge bg-primary">
                {tipo == 1 && 'Salário'}
                {tipo == 2 && 'Férias'}
                {tipo == 3 && 'Rescisão'}
                {tipo == 4 && 'Décimo terceiro'}
                {tipo == 5 && 'PLR'}
                {tipo == 6 && 'Vale/Adiantamento'}
              </div>
            </div>
          </div>
          {holerite && (
            <div className="ms-5 fs-5 text-end">
              <div className="fs-4">
                {holerite.assinado ? (
                  <Icone nome="check-lg" className="text-success" />
                ) : (
                  <Icone nome="x-lg" className="text-danger" />
                )}
                <small className="text-muted fs-6 d-block">Assinado</small>
              </div>
            </div>
          )}
          <div className="ms-5 fs-5 text-end">
            <div className="fs-4">
              {holerite ? (
                <Icone nome="check-lg" className="text-success" />
              ) : (
                <Icone nome="x-lg" className="text-danger" />
              )}
              <small className="text-muted fs-6 d-block">Enviado</small>
            </div>
          </div>
          <div className="ms-2 fs-5 text-end">
            {holerite ? (
              <Botao
                icone="eye-fill"
                tema="primary"
                className="ms-5"
                onClick={() =>
                  window.open(`/holerites/imprimir/${holerite.id}`)
                }
              />
            ) : (
              <Botao
                icone="cloud-upload-fill"
                tema="success"
                className="ms-5"
                onClick={() => setModal(true)}
              />
            )}
          </div>
        </BrowserView>
      </div>
      <Modal lg exibir={modal} titulo="Holerite" aoFechar={setModal}>
        <Form onSubmit={enviar} regras={regras}>
          <div className="row">
            <div className="col-md-12 border-bottom mb-3">
              <Contexto>
                {({ values }) => (
                  <FieldArray name="itens">
                    {({ remove, push }) => (
                      <React.Fragment>
                        {values?.itens?.length > 0 &&
                          values?.itens?.map((item: Item, indice: number) => (
                            <div
                              key={indice.toString()}
                              className={`row ${indice && 'border-top pt-3'}`}
                            >
                              <div className="col-md-11">
                                <div className="row">
                                  <div className="col-md-3">
                                    <Campo
                                      name={`itens.${indice}.codigo`}
                                      rotulo="Código"
                                    />
                                  </div>
                                  <div className="col-md-9">
                                    <Campo
                                      name={`itens.${indice}.descricao`}
                                      rotulo="Descrição"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <Campo
                                      name={`itens.${indice}.referencia`}
                                      rotulo="Referência"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <Campo
                                      name={`itens.${indice}.vencimentos`}
                                      rotulo="Vencimentos"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <Campo
                                      name={`itens.${indice}.descontos`}
                                      rotulo="Descontos"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-1">
                                <Botao
                                  tema="danger"
                                  icone="trash-fill"
                                  onClick={() => remove(indice)}
                                />
                              </div>
                            </div>
                          ))}
                        <div className="col-md-12 mb-3 d-flex justify-content-center gap-2">
                          <Botao
                            tema="success"
                            icone="plus"
                            onClick={() =>
                              push({
                                codigo: '',
                                descricao: '',
                                referencia: '',
                                vencimentos: '',
                                descontos: '',
                              })
                            }
                          >
                            Adicionar item
                          </Botao>
                          {values.itens && (
                            <Botao
                              onClick={() =>
                                calcularValores(values as Holerite)
                              }
                            >
                              Calcular valores
                            </Botao>
                          )}
                        </div>
                      </React.Fragment>
                    )}
                  </FieldArray>
                )}
              </Contexto>
            </div>
            <div className="col-md-4">
              <Campo
                name="salario_base"
                rotulo="Salário base"
                prefixar={<Icone nome="currency-dollar" />}
              />
            </div>
            <div className="col-md-4">
              <Campo
                name="base_calculo_fgts"
                rotulo="Base calc. FGTS"
                prefixar={<Icone nome="currency-dollar" />}
              />
            </div>
            <div className="col-md-4">
              <Campo
                name="fgts_mes"
                rotulo="FGTS do mês"
                prefixar={<Icone nome="currency-dollar" />}
              />
            </div>
            <div className="col-md-4">
              <Campo
                name="base_calculo_inss"
                rotulo="Base calc. INSS"
                prefixar={<Icone nome="currency-dollar" />}
              />
            </div>
            <div className="col-md-4">
              <Campo
                name="base_calculo_irrf"
                rotulo="Base calc. IRRF"
                prefixar={<Icone nome="currency-dollar" />}
              />
            </div>
            <div className="col-md-4">
              <Campo
                type="number"
                name="total_de_vencimentos"
                rotulo="Total de vencimentos"
                prefixar={<Icone nome="currency-dollar" />}
                forcarValor={vencimentos}
                desabilitarAoForcar={false}
              />
            </div>
            <div className="col-md-4">
              <Campo
                type="number"
                name="total_de_descontos"
                rotulo="Total de descontos"
                prefixar={<Icone nome="currency-dollar" />}
                forcarValor={descontos}
                desabilitarAoForcar={false}
              />
            </div>
            <div className="col-md-4">
              <Campo
                type="number"
                name="liquido"
                rotulo="Valor líquido"
                prefixar={<Icone nome="currency-dollar" />}
                forcarValor={liquido}
                desabilitarAoForcar={false}
              />
            </div>
            <div className="col-md-4">
              <Campo name="faixa_irrf" rotulo="Faixa IRRF" />
            </div>
          </div>

          <div>
            <Botao type="submit">Enviar</Botao>{' '}
            <Botao tema="light" onClick={() => setModal(false)}>
              Cancelar
            </Botao>
          </div>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default UploadLista;
